<template>
  <div class="flex flex-wrap items-center gap-8">
    <span
      class="text-static-default-hi body-2 inline-flex items-center justify-center md:bg-static-default-hi md:rounded-sm md:px-12 md:py-14 lg:py-6"
      data-selector="total-products"
      data-test="total-products"
    >
      {{
        i18n(translations.resultsCount, {
          total: searchNbResults,
        })
      }}
    </span>

    <RevButtonTiny
      v-for="value in removableValues"
      :key="value.value"
      class="hidden lg:block"
      :icon="IconCross"
      variant="secondary"
      @click="onRemoveFilter(value)"
    >
      {{ value.label }}
    </RevButtonTiny>

    <RevLink
      v-if="showClearButtons"
      class="hidden px-12 py-4 lg:block"
      @click="onReset"
    >
      {{ i18n(translations.clearAllFilters) }}
    </RevLink>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { uniqBy } from '@backmarket/utils/collection/uniqBy'
import { RevButtonTiny } from '@ds/components/ButtonTiny'
import { RevLink } from '@ds/components/Link'
import { IconCross } from '@ds/icons/IconCross'

import { FACET_PAGE, FACET_QUERY, FACET_SORT } from './ActiveFilters.constants'
import translations from './ActiveFilters.translations'
import type {
  ActiveFiltersProps,
  RemoveableFilter,
} from './ActiveFilters.types'
import { computeRemovableFilters } from './ActiveFilters.utils'

const i18n = useI18n()

const { trackClick } = useTracking()

const emit = defineEmits(['change', 'reset'])

const props = defineProps<ActiveFiltersProps>()

// We show the clear all button for all selected values, except page, sort & query.
// Note that for price, we don't display any other specific button than the clear all
const showClearButtons = computed(
  () =>
    !!Object.keys(props.filters).filter(
      (filter) => ![FACET_PAGE, FACET_SORT, FACET_QUERY].includes(filter),
    ).length,
)

const filteredFacets = computed(() => uniqBy(props.facets, 'facetName'))

const removableValues = computed(() =>
  computeRemovableFilters(props.filters, filteredFacets.value),
)

function onReset() {
  trackClick({
    name: 'clear_filter',
    zone: 'product list > side filter',
    value: 'clear all filters',
  })

  emit('reset')
}

function onRemoveFilter({ facet, value }: RemoveableFilter) {
  trackClick({
    name: 'clear_filter',
    zone: 'product list > side filter',
    value: facet,
  })

  const filter = props.filters[facet]

  if (Array.isArray(filter)) {
    emit('change', {
      [facet]: filter.filter((val) => val !== value),
    })
  }
}
</script>
